import { memo, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";
import { SmartImage } from "./SmartImage";
import { QualityBadge, Rating } from "./MovieMeta";
import { IconPlay } from "@/components/icons/Icons";
import { ROW_CARD_W } from "@/components/skeleton/Skeletons";
import { setSharedOrigin } from "@/lib/shared";
import { usePrimeMovie } from "@/hooks/useMovies";
import { IS_TOUCH, LOW_POWER } from "@/lib/perf";
import { haptic } from "@/lib/haptics";

function MovieCardBase({ movie, inRow = false, index = 0, priority = false, showProgress = false }) {
  const reduce = useReducedMotion();
  const posterRef = useRef(null);
  const prime = usePrimeMovie();
  const pct = showProgress && movie.progress?.duration ? Math.min(100, (movie.progress.position / movie.progress.duration) * 100) : 0;
  const meta = [movie.year, movie.genres?.[0]?.name].filter(Boolean).join(" · ");

  return (
    <motion.div
      className={cn("group relative", inRow && cn(ROW_CARD_W, "shrink-0 snap-start"))}
      initial={reduce ? { opacity: 0 } : LOW_POWER ? { opacity: 0, y: 10 } : { opacity: 0, filter: "blur(10px)", y: 8 }}
      animate={reduce ? { opacity: 1 } : LOW_POWER ? { opacity: 1, y: 0 } : { opacity: 1, filter: "blur(0px)", y: 0 }}
      transition={{ duration: LOW_POWER ? 0.4 : 0.5, delay: Math.min(index, 8) * 0.03, ease: [0.2, 0.9, 0.2, 1] }}
      data-testid="movie-card"
    >
      <Link
        to={`/movie/${movie.id}`}
        onClick={() => {
          haptic(5);
          prime(movie);
          setSharedOrigin(movie.id, posterRef.current);
        }}
        onMouseEnter={() => prime(movie)}
        className="pk-press block rounded-[20px] outline-none"
        aria-label={`${movie.title}${movie.year ? `, ${movie.year}` : ""}`}
        data-testid="movie-card-link"
      >
        <motion.div
          ref={posterRef}
          whileHover={reduce || IS_TOUCH ? undefined : { scale: 1.03 }}
          whileTap={reduce ? undefined : { scale: 0.97 }}
          transition={{ type: "spring", stiffness: 420, damping: 32 }}
          className="relative aspect-[2/3] overflow-hidden rounded-[20px] shadow-[0_8px_24px_rgba(0,0,0,.35)] ring-1 ring-white/[.06] transition-shadow duration-300 group-hover:shadow-pk-card group-focus-within:ring-white/40"
        >
          <SmartImage src={movie.poster} alt="" fallbackTitle={movie.title} priority={priority} className="absolute inset-0" />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/75 via-black/0 to-black/0 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
          <div className="pointer-events-none absolute inset-x-0 top-0 h-1/3 bg-gradient-to-b from-white/[.07] to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
          {movie.quality?.[0] && (
            <div className="absolute left-2.5 top-2.5">
              <QualityBadge className="border-white/15 bg-black/60">{movie.quality[movie.quality.length - 1]}</QualityBadge>
            </div>
          )}
          {!IS_TOUCH && !LOW_POWER && (
            <div className="absolute inset-x-0 bottom-0 flex items-center justify-between p-3 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
              <span className="grid h-10 w-10 place-items-center rounded-full border border-white/20 bg-black/50 text-white" data-testid="movie-card-play-button">
                <IconPlay size={16} />
              </span>
              <Rating value={movie.rating} className="text-[13px] drop-shadow" />
            </div>
          )}
          {pct > 0 && (
            <div className="absolute inset-x-3 bottom-3 h-1 overflow-hidden rounded-full bg-white/25" data-testid="card-progress">
              <div className="h-full rounded-full bg-white" style={{ width: `${pct}%` }} />
            </div>
          )}
        </motion.div>
        <div className="mt-2.5 px-0.5">
          <h3 className="line-clamp-2 text-[14px] font-bold leading-snug tracking-[-0.01em] text-white sm:text-[14.5px]" title={movie.title}>
            {movie.title}
          </h3>
          {(meta || movie.rating) && (
            <p className="mt-0.5 flex items-center gap-1.5 text-[12.5px] font-medium text-white/50">
              <span className="truncate">{meta}</span>
            </p>
          )}
        </div>
      </Link>
    </motion.div>
  );
}

export const MovieCard = memo(MovieCardBase);
